<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'FileUpload',
})
</script>
<template>
  <FormItemRest>
    <BdBtn :icon="FileAddOutlined" @click="handleOpen">上传文件</BdBtn>
    <div :class="bem('list')" v-if="value.length">
      <FileDisplaySimples v-if="isSimple" :files="value" :col-attrs="filesCol" closeable @del="handleFileDelOne" />
      <FileDisplayNormals v-else :viewRaw="viewRaw" :files="value" :col-attrs="filesCol" closeable @del="handleFileDelOne" />
    </div>
    <Modal
      :visible="visible"
      :class="bem('modal')"
      title="选择文件"
      :footer="null"
      width="1200px"
      destroy-on-close
      centered
      @update:visible="setVisible"
    >
      <Alert type="warning" show-icon closable class="mb-16" message="选择文件或上传成功后将自动关联到表单，无需点击确认，点击关闭按钮或空白处即可" />
      <Tabs v-model:activeKey="activeKey" destroyInactiveTabPane type="card" class="ant-seg-tabs" @change="handleTabChange">
        <Tabs.TabPane :key="TAB_PANE_KEY.WJSC" tab="文件上传">
          <Uploader ref="uploaderRef" @del="handleFileDelOne" @add="handleFileAddOne" />
        </Tabs.TabPane>
        <Tabs.TabPane :key="TAB_PANE_KEY.WJK" tab="文件库">
          <OssList v-model:value="valTmp" />
        </Tabs.TabPane>
        <Tabs.TabPane :key="TAB_PANE_KEY.GPY" tab="高拍仪">
          <Gpy />
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  </FormItemRest>
</template>

<script lang="ts" setup name="FileUpload">
import { computed, ref } from 'vue'
import { createNamespace } from '@bd/vue3-utils'
import { FileAddOutlined } from '@ant-design/icons-vue'
import { Modal, FormItemRest, Form, Tabs, Alert } from 'ant-design-vue'
import { BdBtn } from '-/Common/BdBtn'
import { FileDisplayNormals, FileDisplaySimples } from '-/Common/FileDisplay'
import { fileUploadProps } from './props'
import OssList from './OssList/index.vue'
import Uploader from './Uploader.vue'
import Gpy from './Gpy/index.vue'

const [_, bem] = createNamespace('FileUpload')
const visible = ref(false)
const uploaderRef = ref<InstanceType<typeof Uploader> | null>(null)
/** 记录上一次的 tab key，用于切换失败时恢复 */
let prevTabKey: TAB_PANE_KEY | null = null

const handleOpen = () => {
  visible.value = true
  onFieldBlur()
}
const props = defineProps(fileUploadProps)
const emits = defineEmits(['update:value'])
const { onFieldBlur, onFieldChange } = Form.useInjectFormItemContext()
const valTmp = computed({
  get: () => props.value,
  set: (val: ApiFile.Item[]) => {
    emits('update:value', val)
    onFieldChange()
  }
})

const handleFileDelOne = (id: string) => {
  const items = props.value.filter(item => item.id !== id)
  emits('update:value', items)
}

const handleFileAddOne = (item: ApiFile.Item) => {
  const items = [...props.value, item]
  valTmp.value = items
}

enum TAB_PANE_KEY {
  WJK = 'wjk',
  WJSC = 'wjsc',
  GPY = 'gpy'
}

const activeKey = ref(TAB_PANE_KEY.WJSC)

/**
 * 检查是否有正在上传的文件
 */
function checkUploading(): boolean {
  return uploaderRef.value?.hasUploadingFiles?.() ?? false
}
const setVisible = (val: boolean) => {
  if (val) {
    visible.value = true
  } else {
    handleModalClose()
  }
}
/**
 * 弹窗关闭前的拦截
 */
function handleModalClose() {
  if (checkUploading()) {
    Modal.confirm({
      title: '确认关闭',
      content: '当前有文件正在上传，关闭弹窗将取消所有上传任务，是否继续？',
      okText: '继续关闭',
      centered: true,
      onOk() {
        uploaderRef.value?.cancelAllUploads?.()
        visible.value = false
      }
    })
  } else {
    visible.value = false
  }
}

/**
 * Tab 切换前的拦截
 */
function handleTabChange(newKey: TAB_PANE_KEY) {
  // 只有从「文件上传」tab 切换出去时才需要检查
  if (prevTabKey === TAB_PANE_KEY.WJSC && newKey !== TAB_PANE_KEY.WJSC && checkUploading()) {
    // 阻止切换，恢复到上一个 tab
    activeKey.value = prevTabKey
    Modal.confirm({
      title: '确认切换',
      content: '当前有文件正在上传，切换标签页将取消所有上传任务，是否继续？',
      okText: '继续切换',
      centered: true,
      onOk() {
        uploaderRef.value?.cancelAllUploads?.()
        activeKey.value = newKey
        prevTabKey = newKey
      }
    })
  } else {
    prevTabKey = newKey
  }
}
</script>
<style lang="less">
.file-upload {
  &__modal {
    .ant-modal-body {
      padding: @padding-md @padding-lg;
    }
  }

  &__list {
    margin-top: @margin-md;
  }
}
</style>
